import React from 'react';
import { ArrowRight } from 'react-feather';

import Button from '@/componentsv2/Button';

import { DashboardCard } from './DashboardCard';
import { NetworkIllustration } from './NetworkIllustration';
import { useNetworkExpertCount } from './useNetworkExpertCount';

interface OrgExpertNetworkCardProps {
  internalNetworkId: string;
  collapseButton?: React.ReactNode;
  collapsed?: boolean;
}

// ENG-5288: the per-keyword counts (FacetGrid) badly undersold the network and
// were dropped. The headline total is search-aligned (useNetworkExpertCount
// runs the exact Browse query), resolving the ENG-5249 mismatch that kept the
// previous count hidden.
export function OrgExpertNetworkCard({
  internalNetworkId,
  collapseButton,
  collapsed,
}: OrgExpertNetworkCardProps): React.ReactNode {
  const { count, loading, error, errorDetail, refetch } = useNetworkExpertCount(internalNetworkId);

  return (
    <DashboardCard
      title="Network"
      loading={loading}
      error={error}
      errorDetail={errorDetail}
      onRetry={refetch}
      collapseButton={collapseButton}
      collapsed={collapsed}
    >
      <div className="grid items-center gap-16 md:grid-cols-2">
        <div>
          {/* "0" renders too — an empty network is the CTA to start building it */}
          <div className="flex items-center gap-12">
            {/* text-42 (not the artifact's 64px) per design review feedback */}
            <span className="text-42 font-bold leading-none text-brand-primary">
              {count.toLocaleString()}
            </span>
            <span className="text-14 font-semibold leading-snug text-on-surface">
              experts in your
              <br />
              knowledge network
            </span>
          </div>
          <p className="mt-12 text-12 leading-relaxed text-on-surface-muted">
            Every expert your team has engaged at OnFrontiers. A profile is added the first time
            anyone in your workspace completes a consultation with an expert. Use search to narrow
            in on the right specialist.
          </p>
          <Button
            to={`/search?networks[]=${encodeURIComponent(internalNetworkId)}`}
            endIcon={ArrowRight}
            size="small"
            omitPaddingClassnames
            className="mt-16 w-fit px-12 py-8"
          >
            Browse your full network
          </Button>
        </div>
        <NetworkIllustration className="hidden w-full md:block" />
      </div>
    </DashboardCard>
  );
}
